import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api-client';
import type { RecordQuery } from './api-types';

/**
 * Query keys. Record data is namespaced under the schema version so a schema
 * change (new field, renamed object) invalidates cached record shapes without
 * a manual sweep — the spec's "cache keyed by schema_version".
 */
export const keys = {
  bootstrap: ['bootstrap'] as const,
  schema: ['schema'] as const,
  favorites: ['favorites'] as const,
  records: (version: number, object: string, params: unknown) =>
    ['records', version, object, params] as const,
  record: (version: number, object: string, id: string) =>
    ['record', version, object, id] as const,
  recordContext: (id: string) => ['record-context', id] as const,
  conversations: ['conversations'] as const,
  conversation: (id: string) => ['conversation', id] as const,
  dailyBrief: ['daily-brief'] as const,
  personalization: ['settings', 'personalization'] as const,
  billingUsage: ['settings', 'billing-usage'] as const,
  timeline: (params: unknown) => ['timeline', params] as const,
  search: (query: string, object?: string) => ['search', query, object ?? null] as const,
};

export const bootstrapQuery = () =>
  queryOptions({ queryKey: keys.bootstrap, queryFn: api.bootstrap });

export const schemaQuery = () =>
  queryOptions({
    queryKey: keys.schema,
    queryFn: api.schema,
    // The descriptor also carries live record counts. Treat it as stale so a
    // focus/reconnect always reconciles counters changed on another surface.
    staleTime: 0,
  });

export const recordsQuery = (version: number, object: string, params: { limit?: number; offset?: number; sortField?: string; sortDirection?: 'asc' | 'desc' }) =>
  queryOptions({
    queryKey: keys.records(version, object, params),
    queryFn: () => api.listRecords(object, params),
    placeholderData: (previous) => previous,
  });

export const recordQuery = (version: number, object: string, id: string) =>
  queryOptions({
    queryKey: keys.record(version, object, id),
    queryFn: () => api.getRecord(object, id),
  });

export const recordContextQuery = (id: string) =>
  queryOptions({ queryKey: keys.recordContext(id), queryFn: () => api.recordContext(id) });

export const favoritesQuery = () =>
  queryOptions({ queryKey: keys.favorites, queryFn: api.listFavorites, staleTime: 0 });

export function useSetFavorite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { recordId: string; favorite: boolean }) => {
      if (input.favorite) await api.addFavorite(input.recordId);
      else await api.removeFavorite(input.recordId);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.favorites }),
  });
}

export const conversationsQuery = () =>
  queryOptions({
    queryKey: keys.conversations,
    queryFn: api.listConversations,
    refetchInterval: (query) => query.state.data?.conversations.some((conversation) => conversation.isStreaming)
      ? 1_000
      : false,
  });

export const conversationQuery = (id: string) =>
  queryOptions({
    queryKey: keys.conversation(id),
    queryFn: () => api.getConversation(id),
    // The live SSE viewer owns an originating route. A focus/reconnect refetch
    // during that viewer would duplicate its optimistic rows. Remounted routes
    // fetch normally, and persisted streaming rows poll below.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query) => query.state.data?.conversation.messages.some((message) => message.status === 'streaming')
      ? 1_000
      : false,
  });

export const dailyBriefQuery = () =>
  queryOptions({ queryKey: keys.dailyBrief, queryFn: api.dailyBrief, retry: false, retryOnMount: false });

export const personalizationQuery = () =>
  queryOptions({ queryKey: keys.personalization, queryFn: api.personalization,
    refetchOnMount: 'always', refetchOnWindowFocus: 'always',
    // External outcomes can arrive while Home remains open. This reads saved
    // context only; polling never launches a review or an Auto Progress run.
    refetchInterval: query => query.state.data?.progress?.pending || query.state.data?.learning?.pending ? 2000 : 30_000 });

export const billingUsageQuery = () =>
  queryOptions({ queryKey: keys.billingUsage, queryFn: api.billingUsage });

export const timelineQuery = (params: { record?: string; actorKind?: string; limit?: number } = {}) =>
  queryOptions({ queryKey: keys.timeline(params), queryFn: () => api.timeline(params) });

export const searchQuery = (query: string, object?: string) =>
  queryOptions({
    queryKey: keys.search(query, object),
    queryFn: () => api.search(query, object ? { object } : {}),
    enabled: query.trim().length > 0,
  });

/** Refresh aliases and references too: a renamed record may appear elsewhere. */
export function useUpdateRecord(_version: number, object: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; data: Record<string, unknown> }) =>
      api.updateRecord(object, input.id, input.data),
    onSuccess: (_result, input) => {
      void client.invalidateQueries({ queryKey: keys.personalization });
      void client.invalidateQueries({ queryKey: ['records'] });
      void client.invalidateQueries({ queryKey: ['record'] });
      void client.invalidateQueries({ queryKey: keys.recordContext(input.id) });
      void client.invalidateQueries({ queryKey: keys.favorites });
      void client.invalidateQueries({ queryKey: ['timeline'] });
      void client.invalidateQueries({ queryKey: ['search'] });
    },
  });
}

export function useCreateRecord(_version: number, object: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createRecord(object, data),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.personalization });
      void client.invalidateQueries({ queryKey: ['records'] });
      void client.invalidateQueries({ queryKey: keys.schema });
      void client.invalidateQueries({ queryKey: keys.bootstrap });
      void client.invalidateQueries({ queryKey: ['timeline'] });
      void client.invalidateQueries({ queryKey: ['search'] });
    },
  });
}

export function useDeleteRecord(version: number, object: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteRecord(object, id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.personalization });
      void client.invalidateQueries({ queryKey: ['records', version, object] });
      void client.invalidateQueries({ queryKey: keys.schema });
      void client.invalidateQueries({ queryKey: keys.favorites });
    },
  });
}

export function useDecideActionIntent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; decision: 'approve' | 'reject' }) =>
      api.decideActionIntent(input.id, input.decision),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.dailyBrief });
    },
  });
}

export function useRecordQueryAst(object: string) {
  return useMutation({
    mutationFn: (query: RecordQuery) => api.queryRecords(object, query),
  });
}
